import { useState } from 'react';
import { Mail, Plus } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CapabilityEditor } from '../shared';
import { CanvasserType, PresetKey, type UserCapabilities } from '@signalvote/shared';
import { useVendors, useCreateVendor } from '@/hooks/queries/useVendors';

interface InviteMemberDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onInvite: (data: {
    email: string;
    presetKey: PresetKey | null;
    capabilities: UserCapabilities;
    canvasserType?: CanvasserType;
    vendorId?: string;
  }) => void;
  isPending?: boolean;
  error?: string | null;
}

function isValidEmail(value: string): boolean {
  return value.trim().length > 0 && value.includes('@');
}

export function InviteMemberDialog({
  open,
  onOpenChange,
  onInvite,
  isPending = false,
  error = null,
}: InviteMemberDialogProps) {
  const [email, setEmail] = useState('');
  const [presetKey, setPresetKey] = useState<PresetKey | null>(PresetKey.Volunteer);
  const [capabilities, setCapabilities] = useState<UserCapabilities>({ FieldOps: 2, Analytics: 1 });
  const [showError, setShowError] = useState(false);
  const [canvasserType, setCanvasserType] = useState<CanvasserType>(CanvasserType.Volunteer);
  const [vendorId, setVendorId] = useState<string | undefined>(undefined);
  const [creatingVendor, setCreatingVendor] = useState(false);
  const [newVendorName, setNewVendorName] = useState('');

  const { data: vendors = [] } = useVendors();
  const { mutate: createVendor, isPending: createVendorPending } = useCreateVendor();

  const emailValid = isValidEmail(email);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!emailValid) {
      setShowError(true);
      return;
    }
    onInvite({
      email: email.trim(),
      presetKey,
      capabilities,
      canvasserType,
      vendorId: canvasserType === CanvasserType.Paid ? vendorId : undefined,
    });
  };

  const handleCapabilityChange = (newPreset: PresetKey | null, newCaps: UserCapabilities) => {
    setPresetKey(newPreset);
    setCapabilities(newCaps);
  };

  const handleTypeChange = (value: string) => {
    const newType = value as CanvasserType;
    setCanvasserType(newType);
    if (newType !== CanvasserType.Paid) {
      setVendorId(undefined);
    }
  };

  const handleCreateVendor = () => {
    const trimmed = newVendorName.trim();
    if (!trimmed) return;
    createVendor(
      { name: trimmed },
      {
        onSuccess: (created) => {
          setVendorId(created.id);
          setCreatingVendor(false);
          setNewVendorName('');
        },
      }
    );
  };

  const resetForm = () => {
    setEmail('');
    setPresetKey(PresetKey.Volunteer);
    setCapabilities({ FieldOps: 2, Analytics: 1 });
    setShowError(false);
    setCanvasserType(CanvasserType.Volunteer);
    setVendorId(undefined);
    setCreatingVendor(false);
    setNewVendorName('');
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      resetForm();
    }
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Invite Member</DialogTitle>
            <DialogDescription>
              Send an invitation to join this campaign.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <label htmlFor="invite-email" className="text-sm font-medium">
                Email address <span className="text-destructive">*</span>
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="invite-email"
                  type="email"
                  placeholder="volunteer@example.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (showError) setShowError(false);
                  }}
                  className="pl-9"
                  autoFocus
                />
              </div>
              {showError && !emailValid && (
                <p className="text-sm text-destructive">
                  {email.trim() ? 'Please enter a valid email address' : 'Email address is required'}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Canvasser Type</label>
              <Select
                value={canvasserType}
                onValueChange={handleTypeChange}
                data-testid="canvasser-type-select"
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Volunteer">Volunteer</SelectItem>
                  <SelectItem value="Paid">Paid</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {canvasserType === CanvasserType.Paid && (
              <div className="space-y-2">
                <label className="text-sm font-medium">Vendor</label>
                {!creatingVendor ? (
                  <>
                    <Select
                      value={vendorId ?? 'none'}
                      onValueChange={(value) => {
                        if (value === '__create__') {
                          setCreatingVendor(true);
                        } else {
                          setVendorId(value === 'none' ? undefined : value);
                        }
                      }}
                      data-testid="vendor-select"
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select vendor (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No vendor</SelectItem>
                        {vendors.map((v) => (
                          <SelectItem key={v.id} value={v.id}>
                            {v.name}
                          </SelectItem>
                        ))}
                        <SelectItem value="__create__">
                          <span className="flex items-center gap-1">
                            <Plus className="h-3 w-3" />
                            Create new vendor
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </>
                ) : (
                  <div className="flex gap-2">
                    <Input
                      placeholder="Vendor name"
                      value={newVendorName}
                      onChange={(e) => setNewVendorName(e.target.value)}
                      autoFocus
                    />
                    <Button
                      type="button"
                      size="sm"
                      onClick={handleCreateVendor}
                      disabled={!newVendorName.trim() || createVendorPending}
                    >
                      Add
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setCreatingVendor(false);
                        setNewVendorName('');
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                )}
              </div>
            )}

            <CapabilityEditor
              presetKey={presetKey}
              capabilities={capabilities}
              onChange={handleCapabilityChange}
            />
          </div>

          {error && (
            <div
              role="alert"
              className="mb-3 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
            >
              {error}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isPending || !emailValid}>
              {isPending && (
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
              )}
              Send Invitation
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
